/**
 * The blog: longer pieces than a help answer, in the same plain format. Pure
 * data, shared by the site (/blog and /blog/:slug), the build-time prerender
 * and the sitemap, so a post can never be listed that does not exist.
 *
 * Like the help pages, nothing here quotes Affily's fee rates: the blog is
 * public, and the rates are shown only to signed-in accounts.
 *
 * A post's `body` is a list of blocks: a string is a paragraph, `{ heading }`
 * a section heading, `{ list }` a bulleted list. Inline, `[text](/path)` is a
 * link and `**text**` is bold; nothing else is interpreted.
 *
 * Every post has a `lang` (one of BLOG_LANGUAGES) and a `group`: the versions
 * of one post in different languages share a group, and link to each other.
 * Each is its own page, with its own slug, listed in the sitemap like any other.
 */
import { PROGRAM_TEMPLATES } from './templates.js';
import { INTRO_POSTS } from './blog/introducing-affily.js';

const templateList = PROGRAM_TEMPLATES.map((t) => `**${t.name}.** ${t.recommendedFor}.`);

/**
 * The languages posts are written in, with the few words of page furniture
 * around a post in each. The help pages and the rest of the site stay English.
 */
export const BLOG_LANGUAGES = {
  en: { name: 'English', locale: 'en-GB', allPosts: 'All posts', alsoIn: 'Also in', help: 'Help' },
  ro: { name: 'Română', locale: 'ro-RO', allPosts: 'Toate articolele', alsoIn: 'Și în', help: 'Ajutor' },
  el: { name: 'Ελληνικά', locale: 'el-GR', allPosts: 'Όλα τα άρθρα', alsoIn: 'Επίσης σε', help: 'Βοήθεια' },
  bg: { name: 'Български', locale: 'bg-BG', allPosts: 'Всички статии', alsoIn: 'Също на', help: 'Помощ' },
};

export const BLOG_POSTS = [
  ...INTRO_POSTS,
  {
    slug: 'store-integrations-and-brands',
    lang: 'en',
    group: 'store-integrations',
    title: 'Every way to connect your store to Affily, and the brands it’s built for',
    summary:
      'Shopify, WooCommerce and Stripe in one click, signed postbacks from your own server, a browser tag for sites with no server, and the kinds of brand Affily runs programs for.',
    publishedAt: '2026-10-25',
    body: [
      'A creator program only works if the sales come back. Every commission on Affily starts with a sale reaching us, so the first thing a brand sets up after its terms is how its store reports orders. Here is every way to do that, best first, and the kinds of brand each one suits.',

      { heading: 'One click: Shopify, WooCommerce and Stripe' },
      'If you sell on Shopify or WooCommerce, or take payments with Stripe, open your program’s **Tracking & stores** page and click your platform. You approve Affily on the platform’s own screen, and Affily sets up the webhooks itself. There is no code to add.',
      {
        list: [
          '**Every paid order is reported** from the platform, not from the shopper’s browser, so ad blockers can’t stop it.',
          '**Stripe subscriptions renew on their own.** Each renewal is credited to the creator who brought the first sale, for as long as your program’s recurring window runs.',
          '**Creator codes are created in your store for you.** When a creator joins, their code (say RAE10) appears in Shopify, WooCommerce or Stripe, and an order that uses it is credited to them with no click needed.',
        ],
      },
      'On Stripe, a sale made with a creator code needs nothing else. To credit sales from a creator’s link as well, pass the `aff_click` value from your landing page into the Checkout Session, as `client_reference_id` or `metadata.aff_click`.',

      { heading: 'Paste one secret: Shopify or Stripe webhooks' },
      'Prefer not to approve an app? Create one webhook in your own Shopify or Stripe dashboard and paste its signing secret into Affily. Shopify needs nothing else, because it already records the landing address on each order, and that address carries the creator’s click.',

      { heading: 'Your own server: the signed postback' },
      'If you run your own checkout, your server tells Affily about each sale with one signed request. It needs no API key. Each program has a signing secret on its integration page, and that secret is what proves the sale came from you.',
      {
        list: [
          '**Retries are safe.** Each order is counted once per program, so a retry, or a postback and a tag both firing, still pays one commission.',
          '**Subscriptions:** send the same `subscription_id` with the first sale and every renewal, and renewals are credited without a click.',
          '**Creator codes:** send the code used at checkout as `code=`. A code that belongs to a creator credits them, and wins over a click.',
        ],
      },
      'The Tracking & stores page has ready-to-paste guides for **Node / Express**, **Python (Django or Flask)**, **Ruby on Rails** and **WordPress / WooCommerce**, plus a plain **curl** example for anything else.',

      { heading: 'No server: the browser tag' },
      'For sites with no server of their own, a small script tag reports actions from the page. There are guides for **Squarespace**, **Webflow, Carrd and static sites**, and **React or other single-page apps**.',
      'Treat it as the last resort. A browser tag can be blocked, can’t keep a secret, and typically misses 20–40% of conversions. It is fine for actions with no money attached, like a signup or a booked viewing. For purchases, use a connection or the postback.',

      { heading: 'Sales only you can see' },
      'Some sales never touch a website: a deal closed by phone, a signed contract, a sale at a viewing. Mark that action as one only you record, and Affily refuses it from tags, postbacks and stores, since anyone can fire a tag. You record it in your dashboard from the tracked action it followed, such as the viewing a creator booked. It is credited to that creator however long afterwards, even after the program has ended, as long as the first action happened while the program ran.',

      { heading: 'The brands Affily is built for' },
      'Each kind of program starts from a template with sensible terms, which you then change to your own. They cover:',
      { list: templateList },
      'Programs pay in **euros, US dollars or pounds**. Each program picks one when it is created.',
      '**Apps and games** have no one-click connection to the App Store or Google Play yet. If your server already hears about in-app purchases, for example from your payment provider’s webhooks, it can send them as signed postbacks with the creator code the player entered.',

      { heading: 'Getting started' },
      '[Set up your brand and first program](/start/brand). Saving the program takes you straight to Tracking & stores, so you can connect your store before any creator arrives. Questions? The [help pages](/help) have the details.',
    ],
  },
];

export function getBlogPost(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

/** The posts in one language, newest first. */
export function blogPostsIn(lang) {
  return BLOG_POSTS.filter((p) => p.lang === lang);
}

/** The same post in the other languages it is written in, in BLOG_LANGUAGES order. */
export function blogTranslations(post) {
  const order = Object.keys(BLOG_LANGUAGES);
  return BLOG_POSTS.filter((p) => p.group === post.group && p.slug !== post.slug).sort(
    (a, b) => order.indexOf(a.lang) - order.indexOf(b.lang),
  );
}

/** A post as plain text, for descriptions and search engines. */
export function blogPlainText(post) {
  return [post.summary, ...post.body.flatMap((b) => (typeof b === 'string' ? [b] : b.list ?? []))]
    .join(' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1');
}
